import React, { useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { startOfDay, endOfDay, addMinutes, isSameDay, isBefore } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { useTranslation } from 'react-i18next'

import DateField from '../../../FormLegacy/FormFieldsV2/DateField'

import Note from './Note'

const CustomForm = ({ values, setFieldValue, selectedBuilding }) => {
  const { t } = useTranslation(['visitors'])
  // Helper to get current time in building timezone
  const getBuildingTime = useCallback(
    () => toZonedTime(new Date(), selectedBuilding.timezone),
    [selectedBuilding.timezone],
  )

  const [minEndTime, setMinEndTime] = useState(() =>
    startOfDay(toZonedTime(new Date(), selectedBuilding.timezone)),
  )
  const [maxEndTime, setMaxEndTime] = useState(() =>
    endOfDay(toZonedTime(new Date(), selectedBuilding.timezone)),
  )
  const [minStartTime, setMinStartTime] = useState(() =>
    startOfDay(toZonedTime(new Date(), selectedBuilding.timezone)),
  )
  const [maxStartTime, setMaxStartTime] = useState(() =>
    endOfDay(toZonedTime(new Date(), selectedBuilding.timezone)),
  )

  const isSelectedDateToday = values.dueDate && isSameDay(values.dueDate, getBuildingTime())

  useEffect(() => {
    if (isSelectedDateToday) {
      const now = getBuildingTime()
      // Note: Round current time down to nearest 15-minute interval for more strict validation
      const currentMinutes = now.getMinutes()
      const roundedMinutes = Math.floor(currentMinutes / 15) * 15
      const roundedNow = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        now.getHours(),
        roundedMinutes,
      )

      if (values.timeStart && isBefore(values.timeStart, addMinutes(roundedNow, -15))) {
        setFieldValue('timeStart', null)
      }

      const minEndTime = addMinutes(roundedNow, 15)
      if (values.timeEnd && isBefore(values.timeEnd, minEndTime)) {
        setFieldValue('timeEnd', null)
      }
    }
  }, [isSelectedDateToday, values.timeStart, values.timeEnd, setFieldValue, getBuildingTime])

  useEffect(() => {
    if (!values.keyAccess) return

    const now = getBuildingTime()
    const selectedStartDate =
      values.startOfDay instanceof Date ? values.startOfDay : new Date(values.startOfDay || now)

    const isStartDateToday = isSameDay(selectedStartDate, now)

    if (values.timeStart instanceof Date) {
      const syncedTimeStart = new Date(
        selectedStartDate.getFullYear(),
        selectedStartDate.getMonth(),
        selectedStartDate.getDate(),
        values.timeStart.getHours(),
        values.timeStart.getMinutes(),
      )
      if (syncedTimeStart.getTime() !== values.timeStart.getTime()) {
        setFieldValue('timeStart', syncedTimeStart)
      }
    }

    const minTime = isStartDateToday ? addMinutes(now, 15) : startOfDay(selectedStartDate)
    const maxTime = endOfDay(selectedStartDate)

    setMinStartTime(minTime)
    setMaxStartTime(maxTime)
  }, [values.startOfDay, values.timeStart, values.keyAccess, setFieldValue, getBuildingTime])

  useEffect(() => {
    if (!values.keyAccess) return

    const now = getBuildingTime()
    const selectedEndDate =
      values.endOfDay instanceof Date ? values.endOfDay : new Date(values.endOfDay || now)

    const selectedStartDate =
      values.startOfDay instanceof Date ? values.startOfDay : new Date(values.startOfDay || now)

    const isEndDateToday = isSameDay(selectedEndDate, now)
    const isSameSelectedDay = isSameDay(selectedEndDate, selectedStartDate)

    if (isBefore(selectedEndDate, selectedStartDate)) {
      setFieldValue('endOfDay', null)
      setFieldValue('timeEnd', null)
      return
    }

    let minTime
    if (isSameSelectedDay && values.timeStart instanceof Date) {
      minTime = addMinutes(values.timeStart, 15)
    } else if (isEndDateToday) {
      minTime = addMinutes(now, 15)
    } else {
      minTime = startOfDay(selectedEndDate)
    }

    const maxTime = endOfDay(selectedEndDate)

    if (values.timeEnd instanceof Date) {
      const sameDayAsEnd = isSameDay(values.timeEnd, selectedEndDate)

      if (!sameDayAsEnd) {
        const syncedTimeEnd = new Date(
          selectedEndDate.getFullYear(),
          selectedEndDate.getMonth(),
          selectedEndDate.getDate(),
          values.timeEnd.getHours(),
          values.timeEnd.getMinutes(),
        )

        if (!isBefore(syncedTimeEnd, minTime)) {
          setFieldValue('timeEnd', syncedTimeEnd)
        }
      }
    }
    if (
      values.timeEnd &&
      isSameDay(values.timeEnd, selectedEndDate) &&
      isBefore(values.timeEnd, minTime)
    ) {
      setFieldValue('timeEnd', null)
    }

    setMinEndTime(minTime)
    setMaxEndTime(maxTime)
  }, [
    values.endOfDay,
    values.timeEnd,
    values.timeStart,
    values.startOfDay,
    values.keyAccess,
    setFieldValue,
    getBuildingTime,
  ])

  return (
    <div className="visitor-wrapper">
      <div>
        <div className="columns visitorMarginTop">
          {!values.keyAccess ? (
            <React.Fragment>
              <div className="column is-4">
                <div className="input-container">
                  <p className="input-label">{t('visitors:form.dateLabel')}</p>
                  <DateField
                    name="dueDate"
                    placeholderText={t('visitors:form.endDateFormat')}
                    autoComplete="off"
                    dateFormat="MM/dd/yyyy"
                    minDate={getBuildingTime()}
                    selected={values.dueDate}
                    onChange={(e) => {
                      setFieldValue('dueDate', e.target.value)
                    }}
                    onChangeRaw={(e) => e.preventDefault()}
                    values={values.dueDate}
                  />
                </div>
              </div>
              <div className="column is-4">
                <div className="input-container">
                  <p className="input-label">{t('visitors:form.startTimeLabel')}</p>
                  <DateField
                    name="timeStart"
                    values={values.timeStart}
                    selected={values.timeStart}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption={t('visitors:form.timeCaption')}
                    dateFormat="h:mm aa"
                    onChange={(time) => {
                      setFieldValue('timeStart', time.target.value)
                    }}
                    placeholderText={t('visitors:form.selectTime')}
                    minTime={
                      isSelectedDateToday ? getBuildingTime() : startOfDay(getBuildingTime())
                    }
                    maxTime={endOfDay(getBuildingTime())}
                  />
                </div>
              </div>
              <div className="column is-4">
                <div className="input-container">
                  <p className="input-label">{t('visitors:form.endTimeLabel')}</p>
                  <DateField
                    name="timeEnd"
                    values={values.timeEnd}
                    selected={values.timeEnd}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption={t('visitors:form.timeCaption')}
                    dateFormat="h:mm aa"
                    onChange={(time) => {
                      setFieldValue('timeEnd', time.target.value)
                    }}
                    placeholderText={t('visitors:form.selectTime')}
                    minTime={
                      values.timeStart
                        ? addMinutes(values.timeStart, 15)
                        : isSelectedDateToday
                          ? addMinutes(getBuildingTime(), 15)
                          : startOfDay(getBuildingTime())
                    }
                    maxTime={endOfDay(getBuildingTime())}
                  />
                </div>
              </div>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <div className="visitor-custom-group">
                <div className="column">
                  <div className="input-container">
                    <p className="input-label">{t('visitors:form.startDate')}</p>
                    <DateField
                      name="startOfDay"
                      placeholderText={t('visitors:form.endDateFormat')}
                      autoComplete="off"
                      dateFormat="MM/dd/yyyy"
                      minDate={getBuildingTime()}
                      selected={values.startOfDay}
                      onChange={(e) => setFieldValue('startOfDay', e.target.value)}
                      onChangeRaw={(e) => e.preventDefault()}
                      values={values.startOfDay}
                    />
                  </div>
                </div>
                <div className="column">
                  <div className="input-container">
                    <p className="input-label">{t('visitors:form.startTimeLabel')}</p>
                    <DateField
                      name="timeStart"
                      values={values.timeStart}
                      selected={values.timeStart}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={15}
                      timeCaption={t('visitors:form.timeCaption')}
                      dateFormat="h:mm aa"
                      onChange={(time) => setFieldValue('timeStart', time.target.value)}
                      placeholderText={t('visitors:form.selectTime')}
                      minTime={minStartTime}
                      maxTime={maxStartTime}
                    />
                  </div>
                </div>
                <div className="column">
                  <div className="input-container">
                    <p className="input-label">{t('visitors:form.endDate')}</p>
                    <DateField
                      name="endOfDay"
                      placeholderText={t('visitors:form.endDateFormat')}
                      autoComplete="off"
                      dateFormat="MM/dd/yyyy"
                      minDate={values.startOfDay || getBuildingTime()}
                      selected={values.endOfDay}
                      onChange={(e) => setFieldValue('endOfDay', e.target.value)}
                      onChangeRaw={(e) => e.preventDefault()}
                      values={values.endOfDay}
                    />
                  </div>
                </div>
                <div className="column">
                  <div className="input-container">
                    <p className="input-label">{t('visitors:form.endTimeLabel')}</p>
                    <DateField
                      name="timeEnd"
                      values={values.timeEnd}
                      selected={values.timeEnd}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={15}
                      timeCaption={t('visitors:form.timeCaption')}
                      dateFormat="h:mm aa"
                      onChange={(time) => setFieldValue('timeEnd', time.target.value)}
                      placeholderText={t('visitors:form.selectTime')}
                      minTime={minEndTime}
                      maxTime={maxEndTime}
                    />
                  </div>
                </div>
              </div>
            </React.Fragment>
          )}
        </div>
        <Note values={values} setFieldValue={setFieldValue} />
      </div>
    </div>
  )
}

CustomForm.propTypes = {
  values: PropTypes.shape({
    dueDate: PropTypes.instanceOf(Date),
    timeStart: PropTypes.instanceOf(Date),
    timeEnd: PropTypes.instanceOf(Date),
    startOfDay: PropTypes.instanceOf(Date),
    endOfDay: PropTypes.instanceOf(Date),
    keyAccess: PropTypes.bool,
    isVisitorNoteOpen: PropTypes.bool,
    visitorNote: PropTypes.string,
  }).isRequired,
  setFieldValue: PropTypes.func.isRequired,
  selectedBuilding: PropTypes.object.isRequired,
}

export default CustomForm
